import React from 'react';
import { Box, Typography, Paper, alpha } from '@mui/material';
import { COLORS, FONT_SIZES, FONT_WEIGHTS } from '../../theme/constants';

/**
 * FormSection - A modern styled form section with title and description
 * Features: Gradient accents, optional paper wrapper, icon support
 */
const FormSection = ({
  title,
  description,
  icon: Icon,
  children,
  variant = 'default', // 'default' | 'outlined' | 'filled'
  color = COLORS.primary.main,
  collapsible = false,
  sx = {},
}) => {
  const content = (
    <>
      {(title || description) && (
        <Box sx={{ mb: 2.5 }}>
          {title && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: description ? 0.75 : 0 }}>
              {Icon && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.7)})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 2px 8px ${alpha(color, 0.3)}`,
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: '#fff' }} />
                </Box>
              )}
              <Typography
                sx={{
                  fontWeight: FONT_WEIGHTS.semiBold,
                  fontSize: FONT_SIZES.h5,
                  color: COLORS.text.primary,
                }}
              >
                {title}
              </Typography>
            </Box>
          )}
          {description && (
            <Typography
              sx={{
                fontSize: FONT_SIZES.body2,
                color: COLORS.text.secondary,
                lineHeight: 1.6,
                ml: Icon ? 5.75 : 0,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      )}
      {children}
    </>
  );

  if (variant === 'outlined') {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${COLORS.border.light}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: 4,
            background: `linear-gradient(180deg, ${color}, ${alpha(color, 0.5)})`,
          },
          ...sx,
        }}
      >
        {content}
      </Paper>
    );
  }

  if (variant === 'filled') {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${COLORS.background.paper} 100%)`,
          border: `1px solid ${alpha(color, 0.15)}`,
          ...sx,
        }}
      >
        {content}
      </Paper>
    );
  }

  // Default variant
  return (
    <Box sx={{ mb: 4, ...sx }}>
      {content}
    </Box>
  );
};

export default FormSection;
